import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DollarSign, Flag, Loader2, CreditCard, Lock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export type PaymentMode = "none" | "deposit" | "progress";

interface PaymentPlanPanelProps {
  mode: PaymentMode;
  onModeChange: (mode: PaymentMode) => void;
  depositContent: React.ReactNode;
  progressContent: React.ReactNode;
  milestoneBadge?: React.ReactNode;
  isModeChangePending?: boolean;
  isProgressDisabled?: boolean;
  isProgressGated?: boolean;
  isRecurringActive?: boolean;
}

const RECURRING_BLOCK_TOOLTIP =
  "Turn off Recurring first to use a payment plan.";

// The tab icons drop out when the side column is narrow (around 1024px
// screens) so "None / Deposit / Milestones" still fits on one line.
const TAB_ICON = "w-3 h-3 shrink-0 hidden @min-[13.5rem]:block";

export function PaymentPlanPanel({
  mode,
  onModeChange,
  depositContent,
  progressContent,
  milestoneBadge,
  isModeChangePending = false,
  isProgressDisabled = false,
  isProgressGated = false,
  isRecurringActive = false,
}: PaymentPlanPanelProps) {
  const depositBlocked = isRecurringActive;
  const progressBlocked = isProgressDisabled || isProgressGated || isRecurringActive;

  const depositButton = (
    <button
      type="button"
      data-walkthrough-target="deposit-btn"
      onClick={() => !depositBlocked && onModeChange("deposit")}
      disabled={depositBlocked}
      className={cn(
        "flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-medium transition-colors",
        mode === "deposit"
          ? "bg-card shadow-sm text-foreground"
          : "text-muted-foreground hover:text-foreground",
        depositBlocked && "opacity-50 cursor-not-allowed"
      )}
    >
      <DollarSign className={TAB_ICON} /> Deposit
    </button>
  );

  const progressButton = (
    <button
      type="button"
      data-walkthrough-target="progress-payments-btn"
      onClick={() => !progressBlocked && onModeChange("progress")}
      disabled={progressBlocked}
      className={cn(
        "flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-medium transition-colors",
        mode === "progress"
          ? "bg-card shadow-sm text-foreground"
          : "text-muted-foreground hover:text-foreground",
        progressBlocked && "opacity-50 cursor-not-allowed"
      )}
    >
      <Flag className={TAB_ICON} />
      Milestones
      {isProgressGated && <Lock className="w-3 h-3 ml-0.5" />}
      {!isProgressGated && milestoneBadge && (
        // The card body shows the same count, so the badge can go when the tabs are tight.
        <span className="hidden @min-[15.5rem]:contents">{milestoneBadge}</span>
      )}
    </button>
  );

  return (
    <Card className="shadow-sm border-border/50">
      <CardHeader className="pb-3 border-b bg-muted/40">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          Payment Plan
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-3">
        <div className="@container flex gap-1 p-1 bg-border rounded-lg">
          <button
            type="button"
            onClick={() => onModeChange("none")}
            className={cn(
              "flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded text-xs font-medium transition-colors",
              mode === "none"
                ? "bg-card shadow-sm text-foreground"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            None
          </button>
          {isRecurringActive ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="flex-1 flex">{depositButton}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                {RECURRING_BLOCK_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          ) : (
            depositButton
          )}
          {isRecurringActive ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="flex-1 flex">{progressButton}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[220px] text-xs">
                {RECURRING_BLOCK_TOOLTIP}
              </TooltipContent>
            </Tooltip>
          ) : isProgressGated ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="flex-1 flex">{progressButton}</span>
              </TooltipTrigger>
              <TooltipContent>Upgrade to Pro to unlock Milestones</TooltipContent>
            </Tooltip>
          ) : isProgressDisabled ? (
            <Tooltip delayDuration={0}>
              <TooltipTrigger asChild>
                <span className="flex-1 flex">{progressButton}</span>
              </TooltipTrigger>
              <TooltipContent>Milestones are not available for this invoice</TooltipContent>
            </Tooltip>
          ) : (
            progressButton
          )}
        </div>

        {isModeChangePending ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {mode === "none" && (
              <p className="text-xs text-muted-foreground text-center py-2">
                No payment plan set. Select Deposit or Milestones above.
              </p>
            )}
            {mode === "deposit" && depositContent}
            {mode === "progress" && progressContent}
          </>
        )}
      </CardContent>
    </Card>
  );
}
